<script setup lang="ts">
import ModalShell from '~/components/property/access/modals/ModalShell.vue';
import type { ExclusiveUnitRow } from '~/types/exclusives';

// Unit popup on 100% Commission projects (meeting 2026-11, like the CRM): layout, the unit's own payment plan,
// "Prepare sales offer" and "Book the unit" (WhatsApp to the project's booking contact with a fixed message).
const {
  unit,
  bookingContactName = null,
  canHold = false,
  hideOffer = false,
} = defineProps<{
  unit: ExclusiveUnitRow;
  bookingContactName?: string | null;
  canHold?: boolean;
  hideOffer?: boolean;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'prepare-offer', unit: ExclusiveUnitRow): void;
  (e: 'hold', unit: ExclusiveUnitRow): void;
}>();

const { t } = useI18n();
const titleId = useId();
const { formatAed } = useAedAmount();
const { formatNumber } = useFormat();

const lightboxIndex = ref<number | null>(null);

const subtitle = computed(() => [unit.bedrooms, unit.unitType].filter(Boolean).join(' · '));

const facts = computed(() =>
  [
    {
      key: 'price',
      label: t('property.v2Availability.headers.price'),
      value: unit.priceAed ? formatAed(unit.priceAed) : null,
    },
    {
      key: 'area',
      label: t('property.v2Availability.headers.area'),
      value: unit.sizeSqft ? `${formatNumber(Math.round(unit.sizeSqft))} sq.ft` : null,
    },
    {
      key: 'floor',
      label: t('property.v2Availability.headers.floor'),
      value: unit.floor !== null ? String(unit.floor) : null,
    },
    { key: 'view', label: t('property.v2Availability.headers.view'), value: unit.view },
  ].filter((fact): fact is { key: string; label: string; value: string } => !!fact.value),
);

const plan = computed(() => unit.paymentPlanDetail ?? null);

const planCharges = computed(() => {
  const charges: string[] = [];
  const value = plan.value;
  if (value?.dldPercent) charges.push(t('exclusiveUnits.unitDialog.dld', { percent: value.dldPercent }));
  if (value?.adminFeeAed)
    charges.push(t('exclusiveUnits.unitDialog.adminFee', { amount: formatAed(value.adminFeeAed) }));
  return charges.join(' · ');
});

const isAvailable = computed(() => unit.status === 'available');
const bookHint = computed(() =>
  bookingContactName ? t('exclusiveUnits.unitDialog.bookHint', { name: bookingContactName }) : null,
);
</script>

<template>
  <ModalShell
    :labelled-by="titleId"
    @close="emit('close')"
  >
    <h2
      :id="titleId"
      class="ex-title"
    >
      {{ t('exclusiveUnits.unitDialog.title', { unit: unit.unitNumber }) }}
    </h2>
    <p
      v-if="subtitle"
      class="-mt-2 mb-4 text-[13px] text-black-60"
    >
      {{ subtitle }}
    </p>

    <dl class="mb-5 grid grid-cols-2 gap-2">
      <div
        v-for="fact in facts"
        :key="fact.key"
        class="rounded-[10px] bg-[#f8f7f3] px-3 py-2"
      >
        <dt class="font-mono text-[9px] font-semibold uppercase tracking-[0.1em] text-black-60">{{ fact.label }}</dt>
        <dd class="m-0 text-[14px] font-bold tabular-nums text-black-100">{{ fact.value }}</dd>
      </div>
    </dl>

    <section class="mb-5">
      <h3 class="ex-section">{{ t('exclusiveUnits.unitDialog.layout') }}</h3>
      <div
        v-if="unit.floorPlanUrls.length"
        class="grid grid-cols-2 gap-2"
      >
        <button
          v-for="(url, i) in unit.floorPlanUrls"
          :key="url"
          type="button"
          class="aspect-[4/3] overflow-hidden rounded-[10px] border border-black/10 bg-white p-1"
          :aria-label="t('property.v2Availability.viewFloorPlanAriaLabel')"
          @click="lightboxIndex = i"
        >
          <img
            :src="url"
            alt=""
            loading="lazy"
            class="size-full object-contain"
          />
        </button>
      </div>
      <p
        v-else
        class="text-[13px] text-black-60"
      >
        {{ t('exclusiveUnits.unitDialog.noLayout') }}
      </p>
    </section>

    <section class="mb-5">
      <h3 class="ex-section">
        {{ t('exclusiveUnits.unitDialog.paymentPlan') }}<span v-if="plan?.name"> · {{ plan.name }}</span>
      </h3>
      <table
        v-if="plan?.installments.length"
        class="w-full text-[13px]"
      >
        <tbody>
          <tr
            v-for="(step, i) in plan.installments"
            :key="i"
            class="border-b border-black/[0.06] last:border-b-0"
          >
            <td class="py-1.5 pe-2 font-bold tabular-nums text-black-100">{{ step.percentage }}%</td>
            <td class="py-1.5 pe-2 text-black-80">{{ step.label }}</td>
            <td class="py-1.5 text-end tabular-nums text-black-100">
              {{ step.amountAed ? formatAed(step.amountAed) : '' }}
            </td>
          </tr>
        </tbody>
      </table>
      <p
        v-else
        class="text-[13px] text-black-60"
      >
        {{ t('exclusiveUnits.unitDialog.noPaymentPlan') }}
      </p>
      <p
        v-if="planCharges"
        class="mt-1 text-[12px] text-black-60"
      >
        {{ planCharges }}
      </p>
    </section>

    <div class="flex flex-col gap-2 tablet:flex-row">
      <a
        v-if="isAvailable && unit.bookWhatsappUrl"
        :href="unit.bookWhatsappUrl"
        target="_blank"
        rel="noopener"
        class="ex-btn bg-[#25D366] text-white hover:bg-[#1EB454]"
        data-testid="exclusive-unit-book"
      >
        {{ t('exclusiveUnits.unitDialog.bookUnit') }}
      </a>
      <button
        v-if="!hideOffer"
        type="button"
        class="ex-btn bg-black-100 text-white"
        data-testid="exclusive-unit-offer"
        @click="emit('prepare-offer', unit)"
      >
        {{ t('exclusiveUnits.unitDialog.prepareOffer') }}
      </button>
      <button
        v-if="isAvailable && canHold"
        type="button"
        class="ex-btn border border-black/15 bg-white text-black-100"
        @click="emit('hold', unit)"
      >
        {{ t('exclusiveUnits.action.hold') }}
      </button>
    </div>
    <p
      v-if="isAvailable && unit.bookWhatsappUrl && bookHint"
      class="mt-2 text-[12px] text-black-60"
    >
      {{ bookHint }}
    </p>

    <PropertyImageLightbox
      v-if="lightboxIndex !== null"
      :images="unit.floorPlanUrls"
      :start-index="lightboxIndex"
      @close="lightboxIndex = null"
    />
  </ModalShell>
</template>

<style scoped lang="scss">
.ex-title {
  font-family: 'Fraunces', 'Times New Roman', serif;
  font-size: 24px;
  font-weight: 600;
  line-height: 1.15;
  letter-spacing: -0.015em;
  color: #0d0f15;
  margin: 0 0 12px;
  padding-inline-end: 24px;
}

.ex-section {
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(13, 15, 21, 0.62);
  margin: 0 0 8px;
}

.ex-btn {
  display: inline-flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  height: 44px;
  padding: 0 16px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 0.15s ease;
}
</style>
